import React from 'react';
import {
    Button,
    SpaceBetween,
    StatusIndicator,
    BreadcrumbGroup,
    TextContent,
    Header,
    Container,
    Link,
    Alert,
    Box,
} from '@amzn/awsui-components-console';
import { copyToClipboard, LoadingSpinner } from '../../lib/utils';
import '../../../styles/custom.scss';
import CsvDownloader from 'react-csv-downloader';
import { credToCSVData } from './apis';
import { credentials_columns } from '../../lib/constants';
import { useLabAuth } from '../../contexts/LabAuthContext';
import LabPermissionDenied from '../../auth/LabPermissionDenied';

export default function OfficeLab() {
    /**
     * Office lab component
     */
    const { isLabAdmin, isLoading } = useLabAuth();

    if (isLoading) {
        return <LoadingSpinner />;
    }

    if (!isLabAdmin) {
        return <LabPermissionDenied />;
    }

    var stage = 'beta';
    stage = stage === 'test' ? 'alpha' : stage;
    return (
        <SpaceBetween size="s">
            <BreadcrumbGroup
                items={[
                    { text: 'Amazon Device Farm', href: '#/' },
                    {
                        text: 'Office lab setup and management',
                        href: '#/lab-management/office-lab',
                    },
                ]}
            />
            <Header
                variant="h1"
                description="This page provides instructions for Office lab management including get IAM credentails and providing guidance for provisioning and offboarding process."
            >
                Welcome to Amazon Device Farm - Office lab setup and management
            </Header>

            <Container header={<Header variant="h2">Office lab auth steps</Header>}>
                <TextContent>
                    <ol>
                        <li>
                            <SpaceBetween size="xs">
                                <p>
                                    Managing office labs requires authentication. You can generate personalized one time
                                    IAM credentials using the generate credentials button below, which will be{' '}
                                    <StatusIndicator type="info">valid for one hour</StatusIndicator>.
                                </p>
                                <CsvDownloader
                                    filename="office-lab-credential"
                                    extension=".csv"
                                    columns={credentials_columns}
                                    datas={credToCSVData}
                                >
                                    <Button iconName="download" variant="primary">
                                        Generate credentials
                                    </Button>
                                </CsvDownloader>
                                <Alert className="custom-alert" header="Important note">
                                    Do not share or distribute these credentials within or outside Amazon.
                                </Alert>
                            </SpaceBetween>
                        </li>
                        <li>
                            <p>
                                Install required AWS CLI V2 using{' '}
                                <a href="https://docs.aws.amazon.com/cli/latest/userguide/getting-started-install.html">
                                    AWS CLI Install Instructions{' '}
                                </a>
                                and verify it is successful with below command in terminal.
                            </p>
                            {copyToClipboard('aws --version')}
                        </li>
                        <li>
                            <p>Setup AWS credentials for AWS CLI using generated credentials in step 1.</p>
                            {copyToClipboard('export AWS_ACCESS_KEY_ID=<access key id from downloaded credentials>')}
                            {copyToClipboard(
                                'export AWS_SECRET_ACCESS_KEY=<secret access key from downloaded credentials>'
                            )}
                            {copyToClipboard('export AWS_SESSION_TOKEN=<session token from downloaded credentials>')}
                            <Alert className="custom-alert" header="Important note">
                                Do not use <code>aws configure</code> to save the credentials in AWS as it might impact
                                the device farm functionalities.
                            </Alert>
                        </li>
                    </ol>
                </TextContent>
            </Container>
            <Container header={<Header variant="h2">Office lab host provisioning steps</Header>}>
                <TextContent>
                    <ol>
                        <li>
                            Instructions for provisioning a office lab host can be found{' '}
                            <Link
                                external
                                href="https://w.amazon.com/bin/view/DS2DeviceFarm/Technical_Details/ProvisioningHost/"
                            >
                                here
                            </Link>{' '}
                        </li>
                    </ol>
                </TextContent>
            </Container>
            <Container header={<Header variant="h2">Office lab host offboarding steps</Header>}>
                <TextContent>
                    <ol>
                        <li>
                            When a host encounters problem and needs to be replaced or removed, offboarding from device
                            farm is required. Instructions for offboarding a office lab host can be found{' '}
                            <Link
                                external
                                href="https://w.amazon.com/bin/view/DS2DeviceFarm/Technical_Details/Off_Board_Lab/"
                            >
                                here
                            </Link>{' '}
                        </li>
                    </ol>
                </TextContent>
            </Container>
        </SpaceBetween>
    );
}
